import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";

import { EftpayChannel, initiateEftpayChemaDonation } from "./donationsApi";

const EFTPAY_BASE_URL =
  process.env.REACT_APP_EFTPAY_BASE_URL || "https://eu-test.oppwa.com";
const EFTPAY_CARD_BRANDS =
  process.env.REACT_APP_EFTPAY_CARD_BRANDS || "VISA";
const EFTPAY_ECOCASH_BRANDS =
  process.env.REACT_APP_EFTPAY_ECOCASH_BRANDS || "PRIVATE_LABEL";

interface EftpayPaymentFormProps {
  channel: EftpayChannel;
  amount: number | null;
  currency: string;
  email: string;
  streamId: number | null;
  onValidate: () => boolean;
}

/**
 * EFT PAY (EFT Corp / NMBZ OPPWA COPY&PAY) widget slot for the chema donation
 * form. Same COPY&PAY mechanism as ZimSwitch: on "Pay" we prepare a checkout
 * server-side, then inject the OPPWA paymentWidgets.js script + `paymentWidgets`
 * form. The widget renders the hosted card/EcoCash fields and, on submit,
 * redirects to our shopperResultUrl with a `resourcePath` the result page
 * verifies against the backend. The widget must be re-created per checkoutId, so
 * we tear it down on unmount / amount change.
 *
 * `channel` selects the entity (Visa cards vs EcoCash) and which brands the
 * widget offers.
 */
export default function EftpayPaymentForm({
  channel,
  amount,
  currency,
  email,
  streamId,
  onValidate,
}: EftpayPaymentFormProps) {
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [donationId, setDonationId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const brands = channel === "ecocash" ? EFTPAY_ECOCASH_BRANDS : EFTPAY_CARD_BRANDS;
  const label = channel === "ecocash" ? "EcoCash" : "Visa";

  // Tear down any previously injected widget when the checkout changes or the
  // component unmounts — OPPWA leaves globals behind otherwise.
  useEffect(() => {
    if (!checkoutId || !donationId) return;

    const shopperResultUrl = `${window.location.origin}/chema/eftpay/result?donation_id=${donationId}`;

    const script = document.createElement("script");
    script.src = `${EFTPAY_BASE_URL}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;

    const form = document.createElement("form");
    form.action = shopperResultUrl;
    form.className = "paymentWidgets";
    form.setAttribute("data-brands", brands);

    const container = containerRef.current;
    container?.appendChild(form);
    container?.appendChild(script);

    return () => {
      script.remove();
      form.remove();
      // Drop OPPWA's injected widget globals so a fresh checkout re-initialises.
      delete (window as unknown as Record<string, unknown>).wpwlOptions;
      delete (window as unknown as Record<string, unknown>).wpwl;
    };
  }, [checkoutId, donationId, brands]);

  // Reset the widget if the amount, currency or channel changes after a checkout
  // was prepared — a checkout is bound to all three.
  useEffect(() => {
    setCheckoutId(null);
    setDonationId(null);
  }, [amount, currency, channel]);

  const handlePrepare = async () => {
    if (!onValidate() || !amount) return;
    setLoading(true);
    setError(null);
    try {
      const res = await initiateEftpayChemaDonation({
        channel,
        amount,
        currency,
        email: email.trim() || undefined,
        stream_id: streamId,
      });
      setDonationId(res.donation_id);
      setCheckoutId(res.checkout_id);
    } catch (e) {
      setError(
        (e as Error).message || `Could not start ${label} payment. Try again.`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {channel === "ecocash"
          ? "Pay securely with EcoCash. Your payment is processed by the bank's hosted form."
          : "Pay securely with Visa. Card details are processed by the bank's hosted form — we never see your card number."}
      </Typography>

      {!checkoutId ? (
        <Button
          type="button"
          fullWidth
          variant="contained"
          onClick={handlePrepare}
          disabled={loading || !amount}
          sx={{
            mt: 1,
            borderRadius: "999px",
            backgroundColor: "#d97706",
            "&:hover": { backgroundColor: "#b45309" },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
              Preparing...
            </>
          ) : (
            `Pay with ${label}`
          )}
        </Button>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            p: { xs: 1, sm: 1.5 },
          }}
        />
      )}
    </Box>
  );
}
